import { defineStore } from "pinia";
// @ts-ignore
import { ref } from "@vue/runtime-core";
import { useToast } from "vue-toastification";
import api from "@/api/api";

/**
 * Store GLOBAL de suivi des jobs de génération PDF (vectoriel).
 *
 * Contrairement au composable usePdfJobManager (instancié par composant, perdu
 * à la navigation), ce store Pinia est un singleton : le polling et les
 * notifications survivent aux changements de page. Ainsi, l'utilisateur peut
 * enregistrer un support, quitter l'éditeur, et être notifié quand le PDF est
 * prêt.
 */
interface TrackedJob {
  jobId: string;
  supportName: string;
  status: "pending" | "processing" | "completed" | "failed";
  downloadUrl?: string;
  createdAt: number;
}

export const usePdfJobsStore = defineStore("pdfJobs", () => {
  const toast = useToast();
  const jobs = ref<Map<string, TrackedJob>>(new Map());
  // Incrémenté à CHAQUE job terminé (completed). Les vues (ex. « Mes documents »)
  // le watchent pour recharger leur liste et afficher la nouvelle vignette sans
  // devoir quitter puis revenir sur la page.
  const completedTick = ref(0);
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  const hasActive = () =>
    Array.from(jobs.value.values()).some(
      (j) => j.status === "pending" || j.status === "processing"
    );

  const startPolling = () => {
    if (pollTimer) return;
    pollTimer = setInterval(poll, 3000);
  };

  const stopPolling = () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  /** Enregistre un job à suivre et lance le polling + le toast d'attente. */
  const track = (jobId: string, supportName: string) => {
    if (!jobId) return;
    jobs.value.set(jobId, {
      jobId,
      supportName,
      status: "pending",
      createdAt: Date.now(),
    });
    toast.info(
      `Génération du PDF en cours pour « ${supportName} ». Vous serez notifié quand il sera prêt.`,
      { timeout: 6000 }
    );
    startPolling();
  };

  const poll = async () => {
    const pending = Array.from(jobs.value.values()).filter(
      (j) => j.status === "pending" || j.status === "processing"
    );
    if (pending.length === 0) {
      stopPolling();
      return;
    }

    for (const job of pending) {
      try {
        const { data } = await api.get(`/jobs/pdf/${job.jobId}/status`);
        const status = data?.data?.status || data?.status;
        const downloadUrl = data?.data?.download_url || data?.download_url;
        const error = data?.data?.error || data?.error;
        if (!status || status === job.status) continue;

        jobs.value.set(job.jobId, { ...job, status, downloadUrl });

        if (status === "completed") {
          completedTick.value++;
          toast.success(`PDF prêt pour « ${job.supportName} » !`, {
            timeout: 8000,
            closeButton: true,
            onClick: () => downloadUrl && window.open(downloadUrl, "_blank"),
          });
        } else if (status === "failed") {
          toast.error(
            `Échec de la génération du PDF pour « ${job.supportName} » : ${
              error || "erreur inconnue"
            }`,
            { timeout: 8000 }
          );
        }
      } catch (e) {
        // Job introuvable / réseau : on abandonne ce job après 5 min.
        if (Date.now() - job.createdAt > 5 * 60 * 1000) {
          jobs.value.set(job.jobId, { ...job, status: "failed" });
        }
      }
    }

    if (!hasActive()) stopPolling();
  };

  return { jobs, track, completedTick };
});
